/**
 * Dialog modal dùng chung — portal, animation, khóa scroll và layout mặc định/airbnb.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { TEXT } from '../../shared/languages';

export const ActionDialog = ({
  isOpen,
  onClose,
  title,
  children,
  ariaLabel,
  panelClassName = '',
  layout = 'default',
  toolbarStart = null,
}) => {
  const [mounted, setMounted] = useState(isOpen);
  const [visible, setVisible] = useState(false);
  const isAirbnbLayout = layout === 'airbnb';

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      return undefined;
    }

    setVisible(false);
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!mounted || !isOpen) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      setVisible(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [mounted, isOpen]);

  useEffect(() => {
    if (!mounted) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted, onClose]);

  const handlePanelTransitionEnd = (event) => {
    if (event.currentTarget !== event.target) {
      return;
    }

    if (!visible && !isOpen) {
      setMounted(false);
    }
  };

  const handleDismiss = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  const handlePanelClick = (event) => {
    event.stopPropagation();
  };

  if (!mounted) {
    return null;
  }

  const panelClasses = [
    'action-dialog-panel',
    isAirbnbLayout ? 'action-dialog-panel--airbnb' : '',
    panelClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div
      className={`action-dialog ${visible ? 'is-visible' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title}
      onClick={handlePanelClick}
    >
      <button
        type="button"
        className="action-dialog-backdrop"
        aria-label={TEXT.common.close}
        onClick={handleDismiss}
      />
      <div className={panelClasses} onTransitionEnd={handlePanelTransitionEnd} onClick={handlePanelClick}>
        {isAirbnbLayout ? (
          <>
            <div className="action-dialog-airbnb-toolbar">
              <div className="action-dialog-airbnb-toolbar-start">{toolbarStart}</div>
              <button
                type="button"
                className="action-dialog-airbnb-close"
                onClick={handleDismiss}
                aria-label={TEXT.common.close}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="action-dialog-airbnb-content">{children}</div>
          </>
        ) : (
          <>
            {title && (
              <div className="action-dialog-header">
                <h2>{title}</h2>
                <button type="button" className="action-dialog-close" onClick={handleDismiss} aria-label={TEXT.common.close}>
                  ×
                </button>
              </div>
            )}
            <div className="action-dialog-body">{children}</div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
